import { Box, Grid, Typography } from "@mui/material";
import { motion, useReducedMotion } from "motion/react";
import { landingColors } from "../landingColors";
import { trustFeatures } from "../data/trustFeatureData";
import AppScreenshot from "./AppScreenshot";
import SectionContainer from "./SectionContainer";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const FeatureGrid = () => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <SectionContainer id="features">
      <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: landingColors.textPrimary,
            fontSize: { xs: 28, md: 40 },
          }}
        >
          Built for Trust. Made for Growth.
        </Typography>
        <Typography
          sx={{
            color: landingColors.textSecondary,
            mt: 1.5,
            maxWidth: 560,
            mx: "auto",
          }}
        >
          Ngwaahia helps you sell confidently, buy smart, and build loyal
          connections.
        </Typography>
      </Box>

      <Grid
        container
        rowSpacing={{ xs: 10 }}
        columnSpacing={{ xs: 0, sm: 3 }}
        component={motion.div}
        variants={containerVariants}
        initial={prefersReducedMotion ? "visible" : "hidden"}
        whileInView="visible"
        // Triggers off the grid's top edge rather than a fraction of the grid:
        // stacked on a phone, the grid is over 4500px tall, so a fractional
        // `amount` can need more than the screen can show, and the cards would
        // never appear.
        viewport={{ once: true, margin: "0px 0px -15% 0px" }}
      >
        {trustFeatures.map((feature) => (
          <Grid item xs={12} sm={6} md={4} key={feature.title}>
            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                bgcolor: landingColors.screenshotSurface,
                boxShadow: landingColors.cardShadow,
                borderRadius: 3,
                p: 3,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography
                fontWeight={700}
                sx={{
                  color: landingColors.textPrimary,
                  lineHeight: 1.3,
                  // Reserves space for 2 lines at sm/md, where cards sit side
                  // by side in a row narrow enough that a title can wrap (a
                  // title that wrapped would otherwise push that card's
                  // description down relative to the others in the same row).
                  // Not needed at xs, where cards stack into separate rows
                  // with nothing to align against. Not needed at lg+ either:
                  // SectionContainer's content width caps at maxWidth: 1200
                  // (= MUI's `lg` breakpoint), so card width is fixed there
                  // and none of these titles wrap.
                  minHeight: { xs: "auto", sm: "2.6em", lg: "auto" },
                }}
              >
                {feature.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: landingColors.textSecondary,
                  lineHeight: 1.5,
                  // Takes up the card's spare height, so screenshots line up
                  // along the bottom of the row however many lines each
                  // description wraps to at the current width.
                  flexGrow: 1,
                  mb: 1.5,
                }}
              >
                {feature.description}
              </Typography>
              <AppScreenshot
                src={feature.screenshot}
                alt={feature.title}
                platform={feature.platform}
                variant="phone"
                sx={{ maxWidth: 220, mx: "auto" }}
              />
            </Box>
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
};

export default FeatureGrid;
